import React, { useEffect, useState } from 'react';

import { isAuthenticated as hasLiveToken } from '@/services/auth';
import { initializeStore, authService } from '@/stores/storeIntegration';
import { useAppStore, useAuth } from '@/stores/useAppStore';
import { logger } from '@/utils/logger';
import SplashScreen from '@app/components/SplashScreen';
import { registerEmailIntents } from '@features/email/registerEmailIntents';
import { registerNotesIntents } from '@features/notes/registerNotesIntents';
import { registerProjectEditorIntents } from '@features/project-editor/registerProjectEditorIntents';
import { registerWritingBoardIntents } from '@features/writing-board/registerWritingBoardIntents';
import ErrorBoundary from '@shared/components/ErrorBoundary';
import LoadingSpinner from '@shared/components/feedback/LoadingSpinner';
import { Z } from '@shared/constants/zIndex';

import AppProviders from './AppProviders';
import AppRouter from './AppRouter';
import { NavigationBridge, registerBuiltinIntents } from './features/agentUi';

/**
 * Hard ceiling on the cold-boot splash. It is a cap, never a floor: the splash exists
 * to hide a half-drawn shell, so it leaves the moment the shell is ready and this timer
 * only matters when the network stalls.
 */
const SPLASH_MAX_MS = 2_300;
const SPLASH_FADE_MS = 300;

/**
 * True when the persisted store already holds everything the shell needs to paint.
 *
 * Zustand's persist middleware rehydrates synchronously from localStorage while this
 * module evaluates, so the cached user is on hand before the first render. A returning
 * visitor can therefore go straight to the router and revalidate in the background,
 * instead of watching a spinner through a round trip that only confirms what
 * localStorage already said.
 */
const hasWarmSession = (): boolean => {
  const { user, isAuthenticated } = useAppStore.getState();
  return Boolean(user && isAuthenticated && hasLiveToken());
};

/** Exchanges a URL token or revalidates the persisted session, then marks the app booted. */
const bootstrap = async (setBooted: (booted: boolean) => void): Promise<void> => {
  try {
    const token = new URLSearchParams(window.location.search).get('token');
    if (token) {
      await authService.login(token);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // On a warm boot the router is already on screen, so this runs purely as
      // background revalidation and nothing is waiting behind it.
      await initializeStore();
    }
  } catch (error) {
    logger.error('Error initializing app', error);
  } finally {
    setBooted(true);
  }
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();

  const urlParams = new URLSearchParams(window.location.search);
  const hasTokenInUrl = !!urlParams.get('token');

  // Probed once, before paint. A token in the URL is always a cold boot — it has to be
  // exchanged with the server before any of it can be trusted.
  const [isColdBoot] = useState(() => hasTokenInUrl || !hasWarmSession());
  const [booted, setBooted] = useState(!isColdBoot);
  const [showSplash, setShowSplash] = useState(isColdBoot);
  const [fadeSplash, setFadeSplash] = useState(false);

  useEffect(() => {
    registerBuiltinIntents();
    registerNotesIntents();
    registerEmailIntents();
    registerProjectEditorIntents();
    registerWritingBoardIntents();
  }, []);

  useEffect(() => {
    void bootstrap(setBooted);
  }, []);

  // Dismiss the splash as soon as the shell is ready; SPLASH_MAX_MS is the fallback for
  // a bootstrap that never resolves, not the normal path.
  useEffect(() => {
    if (!showSplash) return undefined;

    const delay = booted ? 0 : SPLASH_MAX_MS;
    const fadeTimer = setTimeout(() => setFadeSplash(true), delay);
    const hideTimer = setTimeout(() => setShowSplash(false), delay + SPLASH_FADE_MS);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [booted, showSplash]);

  const splashOverlay = showSplash ? (
    <div
      className="pointer-events-none fixed inset-0"
      style={{
        transition: `opacity ${SPLASH_FADE_MS}ms ease`,
        opacity: fadeSplash ? 0 : 1,
        zIndex: Z.TOAST,
      }}
    >
      <SplashScreen />
    </div>
  ) : null;

  if (!booted) {
    return (
      <AppProviders>
        <ErrorBoundary>
          {splashOverlay}
          <LoadingSpinner fullscreen message="Loading..." />
        </ErrorBoundary>
      </AppProviders>
    );
  }

  return (
    <AppProviders>
      <ErrorBoundary>
        {splashOverlay}
        <NavigationBridge />
        <AppRouter isAuthenticated={isAuthenticated} hasTokenInUrl={hasTokenInUrl} />
      </ErrorBoundary>
    </AppProviders>
  );
};

export default App;
